import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './base.api';
import {
	GoodYouDoProjectResponseDto,
	CreateGoodYouDoProjectDto,
	UpdateGoodYouDoProjectDto,
	UpdateGoodYouDoSequenceDto,
	normalizeGoodYouDoProjectFromApi,
	normalizeGoodYouDoProjectsFromApi
} from '../../models/good-you-do';

const baseUrl = `${process.env.REACT_APP_PROFILE_URL}/api/v2/profile`;

export const goodYouDoApi = createApi({
	reducerPath: 'goodYouDoApi',
	baseQuery: axiosBaseQuery(baseUrl),
	tagTypes: ['goodYouDo'],
	endpoints: (builder) => ({
		getProjects: builder.query<GoodYouDoProjectResponseDto[], number>({
			query: (profileId) => ({
				url: `/${profileId}/goodyoudo`,
				method: 'GET'
			}),
			transformResponse: (response: GoodYouDoProjectResponseDto[], _meta, profileId) =>
				normalizeGoodYouDoProjectsFromApi(profileId, response),
			providesTags: (_result, _error, profileId) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		getProject: builder.query<
			GoodYouDoProjectResponseDto,
			{ profileId: number; projectId: number }
		>({
			query: ({ profileId, projectId }) => ({
				url: `/${profileId}/goodyoudo/${projectId}`,
				method: 'GET'
			}),
			transformResponse: (response: GoodYouDoProjectResponseDto, _meta, arg) =>
				normalizeGoodYouDoProjectFromApi(arg.profileId, response) as GoodYouDoProjectResponseDto,
			providesTags: (_result, _error, { profileId, projectId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: `PROJECT-${projectId}` }
			]
		}),

		createProject: builder.mutation<
			GoodYouDoProjectResponseDto,
			{ profileId: number; request: CreateGoodYouDoProjectDto }
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/goodyoudo`,
				method: 'POST',
				data: request
			}),
			transformResponse: (response: GoodYouDoProjectResponseDto, _meta, arg) =>
				normalizeGoodYouDoProjectFromApi(arg.profileId, response) as GoodYouDoProjectResponseDto,
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		updateProject: builder.mutation<
			GoodYouDoProjectResponseDto,
			{ profileId: number; request: UpdateGoodYouDoProjectDto }
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/goodyoudo`,
				method: 'PUT',
				data: request
			}),
			transformResponse: (response: GoodYouDoProjectResponseDto, _meta, arg) =>
				normalizeGoodYouDoProjectFromApi(arg.profileId, response) as GoodYouDoProjectResponseDto,
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		deleteProject: builder.mutation<
			boolean,
			{ profileId: number; projectId: number; isProfilePublished?: boolean }
		>({
			query: ({ profileId, projectId, isProfilePublished }) => ({
				url: `/${profileId}/goodyoudo/${projectId}`,
				method: 'DELETE',
				params: isProfilePublished ? { isProfilePublished } : undefined
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		updateProjectSequence: builder.mutation<
			void,
			{ profileId: number; projectId: number; request: UpdateGoodYouDoSequenceDto }
		>({
			query: ({ profileId, projectId, request }) => ({
				url: `/${profileId}/goodyoudo/${projectId}/sequence`,
				method: 'PUT',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		})
	})
});

export const {
	useGetProjectsQuery,
	useGetProjectQuery,
	useCreateProjectMutation,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
	useUpdateProjectSequenceMutation
} = goodYouDoApi;

export default goodYouDoApi;
